import {
  Alert,
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  FormController,
  FormLabel,
  Input,
  LoadingButton,
  Radio,
  RadioGroup,
  SelectionControlLabel,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  ExportPurchaseHistory,
  PurchaseHistoryOfflineDownload,
} from '../../../api/oms/order-history';
import { ICSVModalData } from '../../../models/export-csv';
import {
  IExportCSVForm,
  IExportPurchaseHistoryRequest,
  IPHOfflineDownloadRequest,
} from '../../../models/purchase-history';
import {
  getFeatureSwitchValue,
  getWindow,
  redirectToDownloadsPage,
} from '../../../utils/general';
import {
  pushPurchaseHistoryHeaderButtonEvent,
  setExportHistoryError,
} from '../../../utils/purchase-history/analytics';
import { useCommonContext } from '../../../hooks/useContexts';

export const ExportCSVForm = ({
  closeDrawer,
  setAlertState,
  orderHistoryRequest: {
    customerAccountId = '',
    endDate,
    pageNumber,
    pageSize,
    resultsFilter = '',
    searchBy,
    searchType = 'ORDERS',
    sortBy = '',
    sortOrder = 'asc',
    startDate,
  },
  totalNumberOfOrders,
}: IExportCSVForm) => {
  const { userID } = useThdCustomer();
  const [inputError, setInputError] = useState<string>('success');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { timezone } = useCommonContext();

  const generateDefaultFileName = () => {
    const dateTime = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(new Date());

    const [date, time] = dateTime ? dateTime.split('at') : ['', ''];
    const formattedDate = date?.slice(0, -1)?.replace(/[\s,]+/g, '-');
    const formattedTime = time?.slice(1)?.replace(' ', '-').replace(':', '-');
    return `Purchase_History_${formattedDate}_${formattedTime}.csv`;
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      dataType: 'SummaryData',
      fileName: generateDefaultFileName(),
    },
  });
  const watchFileName = watch('fileName');

  useEffect(() => {
    if (errors.fileName || watchFileName === '') {
      setInputError('error');
      return;
    }
    if (inputError === 'error') {
      setInputError('');
    }
  }, [errors, watchFileName]);

  const getCompleteFileName = (fileName: string): string => {
    const endsWithCSV = fileName.includes('.csv');
    return endsWithCSV ? fileName : `${fileName}.csv`;
  };

  const parseBlob = (blobText: string, contentType: string): Blob => {
    return new Blob([blobText], { type: contentType });
  };

  const openFile = (blob: Blob, fileName: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((getWindow()?.navigator as any).msSaveBlob) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (getWindow()?.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      const add = document.createElement('a');
      document.body.appendChild(add);
      add.setAttribute('style', 'display: none');
      const url = (window.URL || window.webkitURL).createObjectURL(blob);
      add.href = url;
      add.download = fileName;
      add.click();

      setTimeout(() => {
        add.remove();
      }, 100);
    }
  };

  const saveFile = (data: string, fileName: string) => {
    const blob = parseBlob(data, 'data:application/csv,');
    return openFile(blob, fileName);
  };

  const closeAlert = () => {
    if (setAlertState) setAlertState(null);
  };

  const handleSuccessfulRequest = (
    response: AxiosResponse,
    fileName: string,
  ): void => {
    setAlertState({
      alertMessage:
        'Purchase History Data successfully exported. Please check your downloads folder for the file.',
      status: 'success',
      onClose: closeAlert,
    });
    if (response.data) {
      const completeFileName = getCompleteFileName(fileName);
      saveFile(response.data, completeFileName);
    }
  };

  const enableAlertError = () => {
    setAlertState({
      alertMessage:
        "We're Sorry. We are unable to export your data. Please try again later.",
      status: 'error',
    });
  };

  const handleStandardExport = (data: ICSVModalData) => {
    const request: IExportPurchaseHistoryRequest = {
      orderHistoryRequest: {
        customerAccountId,
        endDate,
        pageNumber,
        pageSize,
        resultsFilter,
        searchBy,
        searchType,
        sortBy,
        sortOrder,
        startDate,
        includeDetails: data.dataType === 'DetailsData',
        exportType: 'csv',
        timezone,
      },
    };

    ExportPurchaseHistory(request, userID)
      .then((response) => {
        if (response.status && response.data) {
          handleSuccessfulRequest(response, data.fileName);
        }
        if (!response?.status) {
          enableAlertError();
        }
      })
      .catch((error) => {
        enableAlertError();
        setExportHistoryError(error);
      })
      .finally(() => {
        setIsLoading(false);
        setInputError('');
        reset();
        closeDrawer();
      });
  };

  const handleOfflineDownloadExport = (data: {
    dataType: string;
    fileName: string;
  }) => {
    const payload: IPHOfflineDownloadRequest = {
      orderHistoryRequest: {
        customerAccountId,
        startDate,
        endDate,
        includeDetails: data.dataType === 'DetailsData',
        searchType,
        pageSize,
        pageNumber,
        searchBy,
        sortBy,
        sortOrder,
        resultsFilter,
        fileName: data.fileName.replace('.csv', ''),
        timezone,
      },
      offlineProcessRequestType: 'PURCHASE_HISTORY_EXPORT',
      downloadType: 'CSV',
    };

    PurchaseHistoryOfflineDownload(payload, userID)
      .then((response: AxiosResponse) => {
        if (response) {
          redirectToDownloadsPage('orderHistoryRedirect');
        }
      })
      .catch(() => {
        setIsLoading(false);
        setAlertState({
          alertMessage:
            "We're Sorry. We are unable to export your data. Please try again later.",
          status: 'error',
        });
      });
  };

  const onSubmit: SubmitHandler<ICSVModalData> = async (data) => {
    setInputError('success');
    setIsLoading(true);

    pushPurchaseHistoryHeaderButtonEvent(
      `export ${data.dataType === 'DetailsData' ? 'details' : 'summary'}`,
    );

    // Gets the feature switch value for the offline download threshold else sets it to 4000 orders
    const phThreshold =
      (getFeatureSwitchValue('phOfflineDownloadThreshold') as number) || 4000;

    if (totalNumberOfOrders >= phThreshold) {
      handleOfflineDownloadExport(data);
    } else {
      handleStandardExport(data);
    }
  };

  return (
    <>
      <div data-testid="exportCSV">
        <DrawerHeader title="Export Account History" onClose={closeDrawer} />
        <DrawerBody>
          <div style={{ height: '80vh' }}>
            <div className=" sui-mb-6">
              <Alert status="info" size="large">
                Exported data will only show settled transactions. <br />
                Returns and cancellations will not show in exported data.
              </Alert>
            </div>
            <Typography variant="body-base" weight="bold">
              Select data to export
            </Typography>
            <Controller
              control={control}
              name="dataType"
              rules={{ required: true }}
              render={({ field }) => (
                <RadioGroup {...field}>
                  <SelectionControlLabel
                    label="Summary Data"
                    value="SummaryData"
                  >
                    <Radio />
                  </SelectionControlLabel>
                  <SelectionControlLabel
                    label="Details Data"
                    value="DetailsData"
                  >
                    <Radio />
                  </SelectionControlLabel>
                </RadioGroup>
              )}
            />
            <div className="sui-my-3">
              <Typography variant="body-base" color="subtle">
                Data will be exported as a csv file.
              </Typography>
            </div>
            <FormController fullWidth status={inputError}>
              <FormLabel htmlFor="fileName">Filename</FormLabel>
              <Input
                {...register('fileName', { required: true })}
                aria-describedby="fileName-input"
                fullWidth
              />
            </FormController>
            {errors.fileName && (
              <Typography variant="body-xs" color="danger">
                Please enter a filename.
              </Typography>
            )}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <div className="ButtonGroup sui-min-w-full sui-flex sui-gap-4 sui-flex-col">
            <LoadingButton
              onClick={handleSubmit(onSubmit)}
              loading={isLoading}
              variant="primary"
              disabled={errors.fileName || watchFileName === ''}
            >
              Export
            </LoadingButton>
            <Button
              onClick={closeDrawer}
              variant="secondary"
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </DrawerFooter>
      </div>
    </>
  );
};
